import { useEffect } from 'react';
import { toast } from 'sonner';

export const ServiceWorkerManager = () => {
  useEffect(() => {
    // Check if service worker is enabled via environment variable
    const isServiceWorkerEnabled = import.meta.env.VITE_ENABLE_SW === 'true';
    
    if (!isServiceWorkerEnabled) {
      // Unregister existing service workers when disabled
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          registrations.forEach((registration) => {
            registration.unregister();
          });
        });
      }
      return;
    }

    // Register service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/sw.js')
        .then((registration) => {
          console.log('SW registered: ', registration);
          
          // Check for updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  toast.info('New version available! Refresh to update.', {
                    action: {
                      label: 'Refresh',
                      onClick: () => window.location.reload()
                    }
                  });
                }
              });
            }
          });
        })
        .catch((registrationError) => {
          console.log('SW registration failed: ', registrationError);
          // Clear old service worker cache if registration fails
          if ('caches' in window) {
            caches.keys().then(names => {
              names.forEach(name => {
                if (name.includes('billportal')) {
                  caches.delete(name);
                }
              });
            });
          }
        });
    }

    // Handle service worker messages
    const handleMessage = (event: MessageEvent) => {
      const { data } = event;
      if (data) {
        switch (data.type) {
          case 'CACHE_UPDATED':
            toast.success('App updated and ready to use offline!');
            break;
          case 'ASSET_LOADING_ERROR':
            console.warn('Asset loading error detected:', data.url);
            // Immediate action for critical asset failures
            if (data.url && (data.url.includes('.js') || data.url.includes('index-'))) {
              toast.error('Critical asset loading failure. Clearing cache...', {
                duration: 2000,
                  action: {
                    label: 'Reload Now',
                    onClick: () => {
                      const clearCachesAndReload = async () => {
                        if ('caches' in window) {
                          const names = await caches.keys();
                          await Promise.all(names.map(name => caches.delete(name)));
                        }
                        window.location.reload();
                      };
                      clearCachesAndReload();
                    }
                  }
              });
            }
            break;
          case 'OFFLINE_READY':
            toast.info('App is ready to work offline!');
            break;
        }
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleMessage);

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
    };
  }, []);

  return null;
};